import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
} from '@mui/material';
import { CalendarToday, CheckCircle, ErrorOutline, Login as LoginIcon } from '@mui/icons-material';

import { authService } from '@/services/authService';
import { tokenStorage } from '@/services/tokenStorage';
import { sessionStorage } from '@/utils/sessionStorage';
import { useAppDispatch } from '@/hooks/redux';
import { loginSuccess, logout } from '@/store/authSlice';
import { isRunningAsPWA } from '@/utils/pwaDetection';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const code = searchParams.get('code'); // One-time auth code from activation link
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);

  const isPWA = isRunningAsPWA();

  // Handle automatic login after verification
  const handleAutoLogin = useCallback(async (
    accessToken: string,
    refreshToken: string,
    _expiresIn: number
  ) => {
    setAutoLoggingIn(true);

    try {
      // Initialize token storage
      await tokenStorage.initialize();

      // Store refresh token
      await tokenStorage.storeRefreshToken(refreshToken);

      // Fetch user data with the new token
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Save session
        sessionStorage.saveSession(accessToken, data.user);

        // Update Redux state
        dispatch(loginSuccess({
          token: accessToken,
          refreshToken,
          user: data.user,
        }));

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        // If auto-login fails, still show success but let user sign in manually
        console.warn('Auto-login failed, user needs to sign in manually');
        setAutoLoggingIn(false);
      }
    } catch (err) {
      console.error('Auto-login error:', err);
      setAutoLoggingIn(false);
    }
  }, [dispatch, navigate]);

  // Handle code exchange (for activation links)
  const handleCodeExchange = useCallback(async (authCode: string) => {
    try {
      const result = await authService.exchangeCode(authCode);
      await handleAutoLogin(result.accessToken, result.refreshToken, result.expiresIn);
    } catch (err) {
      console.error('Code exchange failed:', err);
      setError('The activation link has expired. Please sign in manually.');
      setLoading(false);
    }
  }, [handleAutoLogin]);

  useEffect(() => {
    const verifyEmail = async () => {
      // If we have a code, exchange it directly (PWA activation link)
      if (code) {
        await handleCodeExchange(code);
        return;
      }

      if (!token) {
        setError('Invalid verification link.');
        setLoading(false);
        return;
      }

      try {
        // Verify email with the token
        const result = await authService.verifyEmail(token) as any;
        setEmail(result.email);
        setSuccess(true);

        // If tokens were returned, attempt auto-login
        if (result.accessToken && result.refreshToken) {
          await handleAutoLogin(result.accessToken, result.refreshToken, result.expiresIn);
        } else {
          // No tokens returned, user needs to sign in manually
          sessionStorage.clearSession();
          dispatch(logout());
          setLoading(false);
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.error?.message
          || err.response?.data?.message
          || 'Failed to verify email. The link may have expired.';
        setError(errorMessage);
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token, code, dispatch, handleAutoLogin, handleCodeExchange]);

  // Show loading spinner while auto-logging in
  if (autoLoggingIn) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{
            m: 1,
            width: 56,
            height: 56,
            bgcolor: '#10b981'
          }}>
            <CalendarToday fontSize="large" />
          </Avatar>
          <Typography component="h1" variant="h4" gutterBottom>
            Player Central
          </Typography>

          <Card sx={{ mt: 3, width: '100%' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Email Verified!
              </Typography>
              <CircularProgress size={24} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Signing you in...
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Link to={isPWA ? '/login' : '/'} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar sx={{
            m: 1,
            width: 56,
            height: 56,
            bgcolor: '#10b981'
          }}>
            <CalendarToday fontSize="large" />
          </Avatar>
          <Typography component="h1" variant="h4" gutterBottom>
            Player Central
          </Typography>
        </Link>

        <Card sx={{ mt: 3, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {loading ? (
              <>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="h6">
                  Verifying your email...
                </Typography>
              </>
            ) : success ? (
              <>
                <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Email Verified
                </Typography>
                <Alert severity="success" sx={{ mb: 3 }}>
                  {email ? `${email} has been verified successfully.` : 'Your email has been verified.'}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Please sign in to access your account.
                </Typography>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button variant="contained" startIcon={<LoginIcon />}>
                    Sign In
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <ErrorOutline color="error" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Verification Failed
                </Typography>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  The verification link may have expired or already been used.
                </Typography>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button variant="contained" sx={{ mr: 1 }} startIcon={<LoginIcon />}>
                    Go to Login
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default VerifyEmailPage;
